import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  X,
  Calendar,
  Shield,
  Globe,
  User,
  Mail,
  MapPin,
  Building,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  ExternalLink,
} from "lucide-react";

DomainDetails.propTypes = {
  domain: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

function DomainDetails({ domain, onClose }) {
  const [activeTab, setActiveTab] = useState("overview");

  // Calculate days until expiration
  const calculateDaysUntilExpiry = () => {
    if (!domain.renewal) return null;
    const today = new Date();
    const expiryDate = new Date(domain.renewal);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilExpiry = calculateDaysUntilExpiry();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl w-full max-w-4xl my-4 sm:my-8">
        {/* Header */}
        <div className="bg-gold-gradient p-4 sm:p-6 rounded-t-xl sm:rounded-t-2xl relative">
          <button
            onClick={onClose}
            className="absolute top-3 right-3 sm:top-4 sm:right-4 p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-full transition-all"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5 text-[#101c4c]" />
          </button>
          <div className="flex items-start sm:items-center gap-3 sm:gap-4 pr-8 sm:pr-0">
            <div className="p-2 sm:p-3 bg-white/20 rounded-lg sm:rounded-xl backdrop-blur-sm flex-shrink-0">
              <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-[#101c4c]" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#101c4c] break-all">
                {domain?.name || "Domain Details"}
              </h1>
              <p className="text-xs sm:text-sm text-[#101c4c]/70 mt-1">
                Manage your domain information
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex gap-1 sm:gap-2 px-3 sm:px-6 overflow-x-auto">
            <button
              onClick={() => setActiveTab("overview")}
              className={`px-3 sm:px-4 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all relative whitespace-nowrap ${
                activeTab === "overview"
                  ? "text-[#b99653] border-b-2 border-[#b99653]"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab("transfer")}
              className={`px-3 sm:px-4 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all relative whitespace-nowrap ${
                activeTab === "transfer"
                  ? "text-[#b99653] border-b-2 border-[#b99653]"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Transfer Status
            </button>
            <button
              onClick={() => setActiveTab("contact")}
              className={`px-3 sm:px-4 py-2 sm:py-3 font-semibold text-xs sm:text-sm transition-all relative whitespace-nowrap ${
                activeTab === "contact"
                  ? "text-[#b99653] border-b-2 border-[#b99653]"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Contact Info
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 max-h-[50vh] sm:max-h-[60vh] overflow-y-auto">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Status Banner */}
              <div
                className={`p-3 sm:p-4 rounded-xl border-2 flex items-start sm:items-center gap-3 ${
                  domain.status === "Active"
                    ? "bg-green-50 border-green-200"
                    : "bg-yellow-50 border-yellow-200"
                }`}
              >
                {domain.status === "Active" ? (
                  <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 text-green-600 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-yellow-600 flex-shrink-0" />
                )}
                <div>
                  <p className="font-semibold text-sm sm:text-base text-gray-900">
                    Status:{" "}
                    <span className="capitalize">
                      {domain.status || "Active"}
                    </span>
                  </p>
                  {daysUntilExpiry !== null && (
                    <p className="text-xs sm:text-sm text-gray-600 mt-0.5">
                      {daysUntilExpiry > 0
                        ? `Expires in ${daysUntilExpiry} days`
                        : "Expired"}
                    </p>
                  )}
                </div>
              </div>

              {/* Domain Information Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* Price */}
                {domain.price && (
                  <div className="bg-gray-50 p-3 sm:p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="p-1.5 sm:p-2 bg-white rounded-lg flex-shrink-0">
                        <span className="text-xl sm:text-2xl">💰</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm text-gray-600">
                          Purchase Price
                        </p>
                        <p className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                          ₹{domain.price.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Registrar */}
                {domain.registrar && (
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg">
                        <Shield className="w-6 h-6 text-[#b99653]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          Current Registrar
                        </p>
                        <p className="text-lg font-semibold text-gray-900">
                          {domain.registrar}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Registered Date */}
                {domain.registered_date && (
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg">
                        <Calendar className="w-6 h-6 text-[#b99653]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Registered Date</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {new Date(
                            domain.registered_date
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Renewal Date */}
                {domain.renewal && (
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg">
                        <Clock className="w-6 h-6 text-[#b99653]" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Renewal Date</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {new Date(domain.renewal).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Category */}
                {domain.category && (
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg">
                        <span className="text-2xl">🏷️</span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Category</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {domain.category}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rating */}
                {domain.rating && (
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-white rounded-lg">
                        <span className="text-2xl">⭐</span>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Domain Rating</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {domain.rating} / 5.0
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Description */}
              {domain.description && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Description
                  </h3>
                  <p className="text-gray-600">{domain.description}</p>
                </div>
              )}

              {/* Specialities */}
              {(domain.speciality || domain.specialities?.length > 0) && (
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <h3 className="font-semibold text-gray-900 mb-3">
                    Specialities
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {domain.speciality && (
                      <span className="bg-[#fcdb6a]/30 text-[#101c4c] text-sm font-semibold px-3 py-1 rounded-full">
                        {domain.speciality}
                      </span>
                    )}
                    {domain.specialities?.map((spec, idx) => (
                      <span
                        key={idx}
                        className="bg-[#fcdb6a]/30 text-[#101c4c] text-sm font-semibold px-3 py-1 rounded-full"
                      >
                        {spec}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Transfer Status Tab */}
          {activeTab === "transfer" && (
            <div className="space-y-6">
              {/* Transfer Info Banner */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <ArrowRight className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      Domain Transfer Process
                    </h3>
                    <p className="text-gray-700 mb-4">
                      Your domain is being transferred to{" "}
                      <span className="font-semibold text-[#b99653]">
                        getbrand.gold
                      </span>{" "}
                      registrar. This process typically takes 5-7 business days.
                    </p>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4" />
                      <span>Started: {new Date().toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transfer Steps */}
              <div className="space-y-4">
                <h3 className="font-semibold text-gray-900">Transfer Steps</h3>

                <div className="space-y-3">
                  {/* Step 1 */}
                  <div className="flex items-start gap-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                    <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Purchase Completed
                      </h4>
                      <p className="text-sm text-gray-600">
                        Your domain purchase has been confirmed and payment
                        received.
                      </p>
                    </div>
                  </div>

                  {/* Step 2 */}
                  <div className="flex items-start gap-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <Clock className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Transfer in Progress
                      </h4>
                      <p className="text-sm text-gray-600">
                        We're initiating the transfer process to getbrand.gold
                        registrar.
                      </p>
                    </div>
                  </div>

                  {/* Step 3 */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                    <div className="w-6 h-6 bg-gray-300 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Registrar Approval
                      </h4>
                      <p className="text-sm text-gray-600">
                        Waiting for approval from the current registrar.
                      </p>
                    </div>
                  </div>

                  {/* Step 4 */}
                  <div className="flex items-start gap-4 p-4 bg-gray-50 border border-gray-200 rounded-xl">
                    <div className="w-6 h-6 bg-gray-300 rounded-full flex-shrink-0 mt-1"></div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        Transfer Complete
                      </h4>
                      <p className="text-sm text-gray-600">
                        Domain successfully transferred to getbrand.gold.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Support */}
              <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-600">
                  <strong>Need help?</strong> If you have any questions about
                  the transfer process, please contact our support team.
                </p>
              </div>
            </div>
          )}

          {/* Contact Information Tab */}
          {activeTab === "contact" && (
            <div className="space-y-6">
              {/* Owner Information */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-[#b99653]" />
                  Domain Owner
                </h3>
                <div className="space-y-3">
                  {domain.ownerName && (
                    <div className="flex items-start gap-3">
                      <User className="w-5 h-5 text-gray-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-gray-600">Owner Name</p>
                        <p className="font-semibold text-gray-900">
                          {domain.ownerName}
                        </p>
                      </div>
                    </div>
                  )}

                  {domain.ownerId && (
                    <div className="flex items-start gap-3">
                      <span className="text-gray-400 mt-0.5">🆔</span>
                      <div>
                        <p className="text-sm text-gray-600">Owner ID</p>
                        <p className="font-mono text-sm text-gray-900">
                          {domain.ownerId}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Registrar Contact */}
              <div className="bg-gray-50 p-6 rounded-xl border border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building className="w-5 h-5 text-[#b99653]" />
                  Registrar Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Building className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Company</p>
                      <p className="font-semibold text-gray-900">
                        getbrand.gold
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Support Email</p>
                      <a
                        href="mailto:support@getbrand.gold"
                        className="font-semibold text-[#b99653] hover:underline"
                      >
                        support@getbrand.gold
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <ExternalLink className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-600">Website</p>
                      <a
                        href="https://getbrand.gold"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold text-[#b99653] hover:underline"
                      >
                        getbrand.gold
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Privacy Notice */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Privacy Protection
                    </h4>
                    <p className="text-sm text-gray-600">
                      Your contact information is protected and will not be
                      publicly displayed in WHOIS records.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 sm:p-6 bg-gray-50 rounded-b-xl sm:rounded-b-2xl">
          <button
            onClick={onClose}
            className="w-full sm:w-auto white-btn !border-[#b99653] hover:bg-[#b99653] hover:text-white transition-all text-sm sm:text-base"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default DomainDetails;
